import * as React from 'react';
import { PrimaryButton } from '@fluentui/react';
import { cloneDeep, isEqual } from 'lodash';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { addKunde, defaultKunde, Kunde, updateKunde, useKundeList } from 'api/kunde';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { ErrorIndicator } from 'layout/ErrorIndicator/ErrorIndicator';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import { Anrede } from 'formComponents/lookups/Anrede';
import { KundeArt } from 'formComponents/lookups/KundeArt';
import { BetreuerById } from 'formComponents/lookups/BetreuerById';
import { LandByLkz2 } from 'formComponents/lookups/LaenderByIkz2';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { KundeKategorie } from 'formComponents/lookups/KundeKategorie';
import { Skontodefinition } from 'formComponents/lookups/Skontodefinition';
import { formStyles } from 'styles/formStyles';
import { FormTextarea, FormTextareaOptions } from 'formComponents/FormTextarea';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { Timestamps } from 'formComponents/Timestamps';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { useHistory } from 'react-router-dom';
import { ValueType } from 'react-select';
import { useBranchenAsOptions } from 'api/branche';
import { useErweiterteSuchenAsOptions } from 'api/erweiterteSuche';
import { useInteresseFuerProdukteAsOptions } from 'api/interesseFuerProdukte';
import { MultiSelect } from 'formComponents/Select/MultiSelect';
import { SelectOption } from 'formComponents/Select/types';
import { Label } from '@fluentui/react/lib/Label';
import {
    branchenByNrSelector,
    erweiterteSuchenByNrSelector,
    interesseFuerProdukteByNrSelector,
} from 'store/lookups/selector';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { Helmet } from 'react-helmet';
import { AraRegelung } from 'formComponents/lookups/AraRegelung';
import { getInstanceConfig, logDifference } from 'utils/helper';
import { KundeSelect } from './KundeSelect';

const styles = mergeStyleSets({
    firstLine: {
        display: 'grid',
        gridTemplateColumns: '80px max-content 1fr 1fr 100px 100px 100px 80px',
        gridColumnGap: 10,
    },
});

interface KundeDetailProps {
    kundeNr: number;
}

export const KundeDetail: React.FC<KundeDetailProps> = ({ kundeNr }) => {
    const auth = useSelector(authSelector);
    const instanceConfig = getInstanceConfig();
    const branchenAsOptions = useBranchenAsOptions();
    const branchenByNr = useSelector(branchenByNrSelector);
    const erweiterteSuchenAsOptions = useErweiterteSuchenAsOptions();
    const erweiterteSuchenByNr = useSelector(erweiterteSuchenByNrSelector);
    const interesseFuerProdukteAsOptions = useInteresseFuerProdukteAsOptions();
    const interesseFuerProdukteByNr = useSelector(interesseFuerProdukteByNrSelector);

    const history = useHistory();

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();

    const kundeList = useKundeList({
        nr: kundeNr,
        includeArchiv: true,
        withBrancheList: true,
        withInteresseFuerProdukteList: true,
        withErweiterteSucheList: true,
    });

    const [kunde, setKunde] = React.useState<Kunde | null>(null);

    React.useEffect(() => {
        if (
            !kundeList.isLoading &&
            !kundeList.isFetching &&
            savingState !== SavingState.saving &&
            (kunde === null || kunde.nr !== kundeNr || savingState === SavingState.saved)
        ) {
            if (kundeList.data?.length === 1) {
                setKunde(cloneDeep(kundeList.data[0]));
                setSavingState(SavingState.notChanged);
            } else {
                setKunde(cloneDeep(defaultKunde));
                setSavingState(SavingState.notSaved);
            }
        }
    }, [kunde, kundeNr, kundeList, savingState]);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !kundeList.isLoading &&
            !kundeList.isFetching &&
            kundeList.data?.length === 1 &&
            kunde !== null &&
            !isEqual(kundeList.data[0], kunde)
        ) {
            setSavingState(SavingState.notSaved);
            logDifference('kunde', kunde, kundeList.data[0]);
        }
    }, [savingState, kundeList, kunde]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const validate = (): boolean => {
        if (instanceConfig.instance !== 'easyloop' && (kunde?.brancheList || []).length === 0) {
            setValidationError('Es muss mindestens eine Branche eingetragen werden.');
            return false;
        }
        if (instanceConfig.instance !== 'easyloop' && !kunde?.land) {
            setValidationError('Es muss ein Land eingetragen werden.');
            return false;
        }
        if (validationError !== null) {
            setValidationError(null);
        }

        return true;
    };

    const save = () => {
        if (kunde && savingState !== SavingState.saving) {
            if (validate()) {
                setSavingState(SavingState.saving);
                if (kundeNr === -1) {
                    addKunde(kunde)
                        .then((result) => {
                            history.push('/kunden/' + result.newNr);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                } else {
                    updateKunde(kunde)
                        .then(() => {
                            if (isMounted) {
                                kundeList.refetch().then(() => setSavingState(SavingState.saved));
                            }
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                }
            }
        }
    };

    if (!kundeNr) {
        return null;
    }

    if (kundeList.isLoading || !kunde) {
        return (
            <div className={formStyles.containerWithSearchBar}>
                <LoadingIndicator />
            </div>
        );
    }

    if (kundeList.isError) {
        return (
            <div className={formStyles.containerWithSearchBar}>
                <ErrorIndicator error={kundeList.error} />
            </div>
        );
    }

    const renderTextField = (column: keyof Kunde, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            label={label}
            value={kunde[column] as string}
            options={options}
            savingState={savingState}
            onChange={(newValue): void => {
                setKunde({
                    ...kunde,
                    [column]: newValue,
                });
            }}
        />
    );

    const renderTextarea = (column: keyof Kunde, label: string, options: FormTextareaOptions = {}) => (
        <FormTextarea
            value={kunde[column] as string}
            savingState={savingState}
            options={options}
            onChange={(newValue): void => {
                setKunde({
                    ...kunde,
                    [column]: newValue,
                });
            }}
            label={label}
        />
    );

    const renderCheckbox2 = (column: keyof Kunde, label: string) => (
        <div>
            <Label>{label}</Label>
            <Checkbox
                className={formStyles.checkboxWithTopLabel}
                disabled={savingState === SavingState.saving}
                checked={kunde[column] as boolean}
                onChange={(event, checked) => {
                    setKunde({
                        ...kunde,
                        [column]: checked || false,
                    });
                }}
            />
        </div>
    );

    /*
    const renderCheckbox = (column: keyof Kunde, label: string) => (
        <Checkbox
            className={formStyles.checkboxWithoutLabel}
            label={label}
            disabled={savingState === SavingState.saving}
            checked={kunde[column] as boolean}
            onChange={(event, checked) => {
                setKunde({
                    ...kunde,
                    [column]: checked || false,
                });
            }}
        />
    );
     */

    const renderAnrede = () => (
        <Anrede
            value={kunde.anrede}
            disabled={savingState === SavingState.saving}
            onChange={(newValue): void => {
                setKunde({
                    ...kunde,
                    anrede: newValue,
                });
            }}
        />
    );

    const renderLand = () => (
        <LandByLkz2
            value={kunde.land}
            disabled={savingState === SavingState.saving}
            onChange={(newValue): void => {
                setKunde({
                    ...kunde,
                    land: newValue,
                });
            }}
        />
    );

    const renderArt = () => (
        <KundeArt
            value={kunde.art}
            disabled={savingState === SavingState.saving}
            onChange={(newValue): void => {
                setKunde({
                    ...kunde,
                    art: newValue,
                });
            }}
        />
    );

    const renderKategorie = () => (
        <KundeKategorie
            value={kunde.kategorie}
            disabled={savingState === SavingState.saving}
            onChange={(newValue): void => {
                setKunde({
                    ...kunde,
                    kategorie: newValue,
                });
            }}
        />
    );

    const renderBetreuer = (column: 'betreuer' | 'telBetreuer', label: string, disabled = false) => (
        <BetreuerById
            value={kunde[column]}
            disabled={disabled || savingState === SavingState.saving}
            label={label}
            onChange={(newValue): void => {
                setKunde({
                    ...kunde,
                    [column]: newValue,
                });
            }}
        />
    );

    const renderSkontodefinition = (column: 'zahlZiel' | 'zahlZielK', label: string) => (
        <Skontodefinition
            value={kunde[column]}
            disabled={savingState === SavingState.saving}
            label={label}
            allowEmpty
            onChange={(newValue): void => {
                setKunde({
                    ...kunde,
                    [column]: newValue,
                });
            }}
        />
    );

    const renderAraRegelung = () => (
        <AraRegelung
            value={kunde?.ara || 0}
            label="ARA Regelung"
            disabled={savingState === SavingState.saving}
            onChange={(newValue): void => {
                if (kunde) {
                    setKunde({
                        ...kunde,
                        ara: newValue,
                    });
                }
            }}
        />
    );

    const renderBrancheList = () => (
        <>
            <Label>Branchen</Label>
            <MultiSelect
                name="brancheList"
                isDisabled={savingState === SavingState.saving}
                value={(kunde?.brancheList || [])
                    .map((nr) => ({ value: nr.toString(), label: branchenByNr[nr].text }))
                    .sort((a, b) => a.label.localeCompare(b.label))}
                placeholder=""
                options={branchenAsOptions}
                onChange={(values: ValueType<SelectOption, true>): void => {
                    setKunde({
                        ...kunde,
                        brancheList: ((values || []) as SelectOption[]).map((option) => parseInt(option.value, 10)),
                    });
                }}
            />
        </>
    );

    const renderErweiterteSucheList = () => (
        <>
            <Label>Erweiterte Suche</Label>
            <MultiSelect
                name="erweiterteSucheList"
                isDisabled={savingState === SavingState.saving}
                value={(kunde?.erweiterteSucheList || [])
                    .map((nr) => ({
                        value: nr.toString(),
                        label: erweiterteSuchenByNr[nr].text,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))}
                placeholder=""
                options={erweiterteSuchenAsOptions}
                onChange={(values: ValueType<SelectOption, true>): void => {
                    setKunde({
                        ...kunde,
                        erweiterteSucheList: ((values || []) as SelectOption[]).map((option) =>
                            parseInt(option.value, 10),
                        ),
                    });
                }}
            />
        </>
    );

    const renderInteresseFuerProdukteList = () => (
        <>
            <Label>Interesse Für Produkte</Label>
            <MultiSelect
                name="interesseFuerProdukteList"
                isDisabled={savingState === SavingState.saving}
                value={(kunde?.interesseFuerProdukteList || [])
                    .map((nr) => ({
                        value: nr.toString(),
                        label: interesseFuerProdukteByNr[nr].text,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))}
                placeholder=""
                options={interesseFuerProdukteAsOptions}
                onChange={(values: ValueType<SelectOption, true>): void => {
                    setKunde({
                        ...kunde,
                        interesseFuerProdukteList: ((values || []) as SelectOption[]).map((option) =>
                            parseInt(option.value, 10),
                        ),
                    });
                }}
            />
        </>
    );

    const renderKonzernSelect = () => (
        <KundeSelect
            label="Konzern"
            kNr={kunde?.konzernNr}
            name={kunde?.konzernName1}
            disabled={savingState === SavingState.saving}
            allowEmpty
            onChange={(newKNr, newKunde): void => {
                if (kunde && kunde.konzernNr !== newKNr) {
                    setKunde({
                        ...kunde,
                        konzernNr: newKNr || 0,
                        konzernName1: newKunde?.name1 || '',
                    });
                }
            }}
        />
    );

    return (
        <div className={formStyles.containerWithSubmenuAndSearchBar}>
            <Helmet>
                <title>{'Kunde ' + kundeNr + ' | Details'}</title>
            </Helmet>

            <div className={formStyles.topBar}>
                <div className={formStyles.topBarLeft}>
                    {validationError && <div className={formStyles.error}>{validationError}</div>}
                    {!validationError && savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}
                </div>
                <div className={formStyles.topBarRight}>
                    {savingState === SavingState.saving ? (
                        <PrimaryButton onClick={save} text="Speichere..." disabled />
                    ) : (
                        <PrimaryButton
                            onClick={save}
                            text="Speichern"
                            disabled={savingState === SavingState.notChanged || kundeList.isLoading}
                        />
                    )}
                </div>
            </div>
            <form className={formStyles.form}>
                <Timestamps
                    createdDate={kunde.anlageDat}
                    createdDecimalTime={kunde.anlageUhr}
                    createdUser={kunde.anlageUser}
                    modifiedDate={kunde.updDat}
                    modifiedDecimalTime={kunde.updUhr}
                    modifiedUser={kunde.updUser}
                />

                <div className={styles.firstLine}>
                    <div className={formStyles.gridCell}>{renderTextField('nr', 'Kunden-Nr.', { disabled: true })}</div>
                    <div className={formStyles.gridCell}>{renderCheckbox2('archiv', 'Archiv')}</div>
                    <div className={formStyles.gridCell}>{renderBetreuer('betreuer', 'Betreuer')}</div>
                    <div className={formStyles.gridCell}>{renderBetreuer('telBetreuer', 'Tel-Betreuer')}</div>
                    <div className={formStyles.gridCell}>{renderAnrede()}</div>
                    <div className={formStyles.gridCell}>{renderArt()}</div>
                    <div className={formStyles.gridCell}>{renderKategorie()}</div>
                    {renderTextField('anzMa', 'Mitarb.Anz.', { numeric: true })}
                </div>

                <div className={formStyles.grid2}>
                    <div className={formStyles.gridCell}>
                        {renderTextField('name1', 'Kundenname 1', { maxLength: 50 })}
                    </div>
                    <div className={formStyles.gridCell}>{renderBrancheList()}</div>
                </div>

                <div className={formStyles.grid3}>
                    <div className={formStyles.gridCell}>
                        {renderTextField('name2', 'Kundenname 2', { maxLength: 50 })}
                    </div>
                    <div className={formStyles.gridCell}>
                        {renderTextField('suchfeld', 'Suchfeld', { maxLength: 50 })}
                    </div>
                    <div className={formStyles.gridCell}>{renderKonzernSelect()}</div>
                </div>

                <div className={formStyles.gridAddressNew}>
                    <div className={formStyles.gridCell}>{renderTextField('plz', 'PLZ', { maxLength: 10 })}</div>
                    <div className={formStyles.gridCell}>{renderTextField('ort', 'Ort', { maxLength: 50 })}</div>
                    <div className={formStyles.gridCell}>
                        {renderTextField('strasse', 'Strasse', { maxLength: 50 })}
                    </div>
                    <div className={formStyles.gridCell}>{renderLand()}</div>
                    <div className={formStyles.gridCell}>
                        {renderTextField('postfach', 'Postfach', { maxLength: 50 })}
                    </div>
                </div>

                <div className={formStyles.grid4}>
                    <div className={formStyles.gridCell}>
                        {renderTextField('telefon', 'Telefon', { maxLength: 50 })}
                    </div>
                    <div className={formStyles.gridCell}>
                        {renderTextField('mail', 'Mailadresse', { maxLength: 50 })}
                    </div>
                    <div className={formStyles.gridCell}>
                        {renderTextField('www', 'Web-Adresse', { maxLength: 50 })}
                    </div>
                    <div className={formStyles.gridCell}>{renderTextField('fax', 'Fax', { maxLength: 50 })}</div>
                </div>

                <div className={formStyles.grid2}>
                    <div className={formStyles.gridCell}>{renderErweiterteSucheList()}</div>
                    <div className={formStyles.gridCell}>{renderInteresseFuerProdukteList()}</div>
                </div>

                <div className={formStyles.grid5}>
                    <div className={formStyles.gridCell}>{renderSkontodefinition('zahlZiel', 'ZZ/Skonto Kunde')}</div>
                    <div className={formStyles.gridCell}>
                        {renderSkontodefinition('zahlZielK', 'ZZ/Skonto Lieferant')}
                    </div>
                    <div className={formStyles.gridCell}>{renderAraRegelung()}</div>
                    <div className={formStyles.gridCell}>{renderTextField('uid', 'UID', { maxLength: 20 })}</div>
                    <div className={formStyles.gridCell}>
                        {renderTextField('fbnr', 'Firmenbuch-Nr.', { maxLength: 15 })}
                    </div>
                </div>

                <div className={formStyles.grid2}>
                    <div className={formStyles.gridCell}>
                        {renderTextField('reclayProduktgruppe', 'Reclay Produktgruppe')}
                    </div>
                </div>

                <div className={formStyles.grid2}>
                    <div className={formStyles.gridCell}>{renderTextarea('bemerkung', 'Bemerkungen')}</div>
                    <div className={formStyles.gridCell}>{renderTextarea('liefBem', 'Liefer-Bemerkungen')}</div>
                </div>

                {auth.rolle === 'ID' ||
                    (auth.rolle === 'GF' && (
                        <>
                            <div className={formStyles.grid4}>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('bhDebKonto', 'Debitorenkonto', { maxLength: 6, disabled: true })}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('bhKredKonto', 'Kreditorenkonto', { maxLength: 6 })}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('kreditlimit', 'Kreditlimit', { numeric: true })}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('eiglKnr', 'Unsere Kd.-Nr.', { maxLength: 10 })}
                                </div>
                            </div>
                            <div className={formStyles.grid5}>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('konto', 'Kontonummer', { maxLength: 50 })}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('blz', 'Bankleitzahl', { maxLength: 20 })}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('bank', 'Name der Bank', { maxLength: 50 })}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('iban', 'IBAN', { maxLength: 50 })}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('bic', 'BIC', { maxLength: 20 })}
                                </div>
                            </div>
                        </>
                    ))}
            </form>

            {routerConfirmMessage.render()}
        </div>
    );
};
